import { format, eachMonthOfInterval, subMonths, isSameMonth } from 'date-fns';

/**
 * Creates an array of dates, one per month. Starts from a numberOfMonths since the finalDate,
 * and ends with the finalDate.
 */
const createDates = (numberOfMonths: number, finalDate: string): Date[] => {
    return eachMonthOfInterval({
        start: subMonths(new Date(finalDate), numberOfMonths - 1), // Subtraction allows for the finalDate to be included
        end: new Date(finalDate),
    });
};

/**
 * Creates an array of dates to be used on the X axis. Takes the total number of historical months, and downsamples that
 * to only return the number of labels desired. The final tick label, typically
 * the most recent estimate, will always be included in the array.
 */
export const createTickLabels = (
    numberOfMonths: number,
    numberOfLabels: number,
    finalTickLabel: string
): Date[] => {
    const dates = createDates(numberOfMonths, finalTickLabel);
    const interval = Math.floor(numberOfMonths / (numberOfLabels - 1));

    return dates
        .reverse() // Ensures the finalTickLabel is always included
        .filter((_, index) => index % interval === 0)
        .reverse();
};

/**
 * Creates an array of data points to display on the graph. Filters a list of estimates to only return
 * those for which there is a label.
 */
export const createDataPoints = (
    estimates: { builtAt?: string | null; currentPrice: number }[],
    labels: Date[]
) => {
    return estimates.filter((estimate) => {
        // Only render a sample of data points, those that we have created a label for
        const isLabelForEstimate = labels.find((label: Date) => {
            if (!estimate.builtAt) return false;
            return isSameMonth(new Date(label), new Date(estimate.builtAt));
        });
        return Boolean(isLabelForEstimate);
    });
};

export const createTooltipLabels = (datum: {
    builtAt: string;
    currentPrice: number;
}) => {
    const date = datum.builtAt
        ? format(new Date(datum.builtAt), 'MMMM yyyy')
        : '';
    const price = formatPrice(datum.currentPrice);
    return `${price}\n${date}`;
};

export const createMinDomain = (dataPoints: { currentPrice: number }[]) => {
    const smallestValue = Math.min(
        ...dataPoints.map(({ currentPrice }) => currentPrice)
    );
    const minusTenPercent = smallestValue - smallestValue / 10;
    const roundedDownToNearest100K =
        Math.floor(minusTenPercent / 100000) * 100000;
    return roundedDownToNearest100K;
};

export const formatPrice = (price: number) => {
    return `£${price.toLocaleString('en-GB')}`;
};

const shortenPrice = (value: number, decimals = 0) => {
    const currency = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        notation: 'compact',
        compactDisplay: 'short',
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
    });
    return currency.format(value).toLowerCase();
};

export const shortenAndFormatPrice = (price: number) => {
    const short =
        price >= 1000000 ? shortenPrice(price, 2) : shortenPrice(price);
    return short;
};

export const formatRentPrice = (price: number, shorten = false) => {
    const rentPrice = formatPrice(price);
    if (shorten) return rentPrice;
    return `${rentPrice}pcm`;
};
